// ─── LIEUX du calendrier (calque plein écran) ─────────────────────
// Gestion des lieux réutilisables (emplacements d'événements et de cours
// d'EDT) : liste + ajout (＋ en-tête) + swipe ✏️/🗑️. Feuille nom + adresse
// avec RECHERCHE (BAN + Photon via @mop/core) → suggestions + coordonnées.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Image, Platform } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, Feuille, GesteRetourEcran, stylesForm,
  useUI, useT, confirmerSuppression,
  rechercherLieu, formatAdresseNominatim, nomPrincipalLieu, adresseCourte, typeIconLieu,
  adresseDepuisCoords, lienItineraire, demanderItineraire, lienCarteEmbed, lienCarteStatique, positionDisponible, positionActuelle,
} from '@mop/core';
import { enregistrerLieux, normaliserLieu, ecouterLieux } from './services/lieux';
import FicheLieuInfo from './FicheLieuInfo';

export default function EcranLieux({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  // Écoute DIRECTE du doc calendrier (même cible que l'écriture) → la liste
  // reflète toujours ce qui est enregistré, quel que soit le calId.
  const [lieux, setLieux] = useState([]);
  const [erreur, setErreur] = useState('');
  useEffect(() => ecouterLieux(calId, setLieux), [calId]);
  const [feuille, setFeuille] = useState(null);   // { initial } | null (édition)
  const [fiche, setFiche] = useState(null);       // lieu affiché en lecture seule
  const [nom, setNom] = useState('');
  const [adresse, setAdresse] = useState('');
  const [coords, setCoords] = useState(null);     // { lat, lng } du lieu choisi
  const [suggestions, setSuggestions] = useState([]);
  const [cherche, setCherche] = useState(false);
  const [gps, setGps] = useState(false);
  const rech = useRef(null);

  // « Ma position » (navigateur/web ; sur mobile natif sans expo-location →
  // indisponible, le bouton est masqué). Remplit l'adresse par géocodage inverse.
  const maPosition = async () => {
    setGps(true);
    const p = await positionActuelle();
    if (p) {
      setCoords(p); setSuggestions([]); setCherche(false);
      const adr = await adresseDepuisCoords(p.lat, p.lng);
      if (adr) setAdresse(adr);
    }
    setGps(false);
  };
  // Choix de l'app de navigation : sans lui, l'OS ouvrait toujours la meme.
  const ouvrirItineraire = (l) => demanderItineraire(l, { titre: t('lieux.itineraire'), annuler: t('commun.annuler') });

  useEffect(() => {
    if (!feuille) return;
    const i = feuille.initial;
    setNom(i?.nom || '');
    setAdresse(i?.adresse || '');
    setCoords(i?.lat != null && i?.lng != null ? { lat: i.lat, lng: i.lng } : null);
    setSuggestions([]); setCherche(false); setErreur('');
  }, [feuille]);

  // Saisie d'adresse : recherche différée (BAN + Photon). Toute frappe manuelle
  // efface les coordonnées tant qu'aucune suggestion n'est choisie.
  const onAdresse = (v) => {
    setAdresse(v); setCoords(null);
    if (rech.current) clearTimeout(rech.current);
    const q = v.trim();
    if (q.length < 3) { setSuggestions([]); setCherche(false); return; }
    setCherche(true);
    rech.current = setTimeout(async () => {
      const res = await rechercherLieu(q);
      setSuggestions(res); setCherche(false);
    }, 350);
  };
  const choisirAdresse = (item) => {
    setAdresse(formatAdresseNominatim(item));
    const lat = parseFloat(item.lat), lng = parseFloat(item.lon);
    setCoords(!Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null);
    setSuggestions([]); setCherche(false);
    if (!nom.trim()) setNom(nomPrincipalLieu(item));
  };

  const enregistrer = async () => {
    const l = normaliserLieu({ ...(feuille?.initial || {}), nom, adresse, lat: coords?.lat, lng: coords?.lng });
    const liste = feuille?.initial?.id ? lieux.map(x => (x.id === l.id ? l : x)) : [...lieux, l];
    try { await enregistrerLieux(calId, liste); setFeuille(null); }
    catch (e) { setErreur(e?.message || String(e)); }
  };
  const supprimer = (l) => confirmerSuppression(t('lieux.supprimerTitre'), t('lieux.supprimerMsg', { nom: l.nom || '' }),
    async () => { try { await enregistrerLieux(calId, lieux.filter(x => x.id !== l.id)); } catch (_) {} });

  const modifie = !!nom.trim() && (nom.trim() !== (feuille?.initial?.nom || '') || adresse.trim() !== (feuille?.initial?.adresse || ''));

  return (
    <GesteRetourEcran actif onRetour={onFermer}>
      <View style={{ flex: 1, backgroundColor: UI.bg }}>
        <PageHeader titre={t('lieux.titre')} iconeTitre="📍" onRetour={onFermer}
          action={{ icone: '＋', label: t('lieux.ajouter'), onPress: () => setFeuille({ initial: null }) }} />
        <ScrollView contentContainerStyle={s.corps}>
          {lieux.length === 0 ? (
            <EmptyState icon="📍" texte={t('lieux.aucunListe')} aide={t('lieux.aucunAide')} />
          ) : lieux.map(l => (
            <LigneSwipe key={l.id} espacement={8} enabled
              onEditer={() => setFeuille({ initial: l })}
              onSupprimer={() => supprimer(l)}>
              <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setFiche(l)}>
                <Text style={{ fontSize: 18 }}>📍</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.carteNom} numberOfLines={1}>{l.nom}</Text>
                  {!!l.adresse && <Text style={s.carteSous} numberOfLines={2}>{l.adresse}</Text>}
                </View>
                {!!lienItineraire(l) && (
                  <TouchableOpacity onPress={() => ouvrirItineraire(l)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityLabel={t('lieux.itineraire')}>
                    <Text style={{ fontSize: 18 }}>🧭</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            </LigneSwipe>
          ))}
        </ScrollView>

        <Feuille visible={!!feuille} titre={feuille?.initial ? t('lieux.modifier') : t('lieux.ajouter')}
          emoji="📍" hauteur={0.8} onFermer={() => setFeuille(null)}
          action={{ icone: feuille?.initial ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !modifie, onPress: enregistrer }}>
          <Text style={s.lbl}>{t('lieux.nom')}</Text>
          <TextInput style={s.input} value={nom} onChangeText={setNom}
            placeholder={t('lieux.phNom')} placeholderTextColor={UI.faint} />
          <Text style={s.lbl}>{t('lieux.adresse')}</Text>
          <View style={s.adrRow}>
            <TextInput style={[s.input, { flex: 1, minHeight: 60 }]} value={adresse} onChangeText={onAdresse} multiline
              placeholder={t('lieux.rechercheAdresse')} placeholderTextColor={UI.faint} />
            {positionDisponible() && (
              <TouchableOpacity style={s.posIcon} onPress={maPosition} disabled={gps} accessibilityLabel={t('lieux.maPosition')}>
                <Text style={{ fontSize: 20, opacity: gps ? 0.4 : 1 }}>📍</Text>
              </TouchableOpacity>
            )}
          </View>
          {cherche && <Text style={s.hint}>{t('lieux.recherche')}</Text>}
          {!!erreur && <Text style={s.err}>{erreur}</Text>}
          {!!coords && (() => {
            // Carte OSM : WebView interactive sur mobile (module présent dans Expo Go),
            // repli image statique ailleurs (web / module absent).
            const embed = lienCarteEmbed(coords);
            let WV = null;
            if (Platform.OS !== 'web' && embed) { try { WV = require('react-native-webview').WebView; } catch { WV = null; } }
            if (WV) return <View style={s.carteWrap}><WV source={{ uri: embed }} style={s.carteImg} /></View>;
            const img = lienCarteStatique(coords);
            return img ? (
              <TouchableOpacity activeOpacity={0.85} onPress={() => ouvrirItineraire({ ...coords, adresse })} style={s.carteWrap}>
                <Image source={{ uri: img }} style={s.carteImg} resizeMode="cover" />
              </TouchableOpacity>
            ) : null;
          })()}
          {suggestions.length > 0 && (
            <View style={s.sugg}>
              {suggestions.map((it, i) => (
                <TouchableOpacity key={(it.place_id || '') + i} style={s.suggItem} activeOpacity={0.7} onPress={() => choisirAdresse(it)}>
                  <Text style={{ fontSize: 16 }}>{typeIconLieu(it)}</Text>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={s.suggNom} numberOfLines={1}>{nomPrincipalLieu(it)}</Text>
                    <Text style={s.suggSous} numberOfLines={1}>{adresseCourte(it) || it.display_name}</Text>
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </Feuille>

        {/* Fiche en lecture seule. Les deux états sont exclusifs : passer à
            l'édition ferme la fiche dans le même rendu — jamais deux feuilles
            superposées sur natif. */}
        <FicheLieuInfo visible={!!fiche} lieu={fiche} onFermer={() => setFiche(null)}
          onEditer={() => { const l = fiche; setFiche(null); setFeuille({ initial: l }); }} />
      </View>
    </GesteRetourEcran>
  );
}

// Styles de formulaire : base partagée du Core + styles propres à l'écran.
const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, padding: 12 },
  carteNom: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  carteSous: { fontSize: 12, color: UI.muted, marginTop: 1 },
  hint: { fontSize: 12, color: UI.faint, marginTop: 6 },
  err: { fontSize: 12, color: UI.danger || '#e5484d', fontWeight: '700', marginTop: 8 },
  carteWrap: { marginTop: 10, borderRadius: 12, overflow: 'hidden', borderWidth: 0.5, borderColor: UI.border },
  carteImg: { width: '100%', height: 150, backgroundColor: UI.card },
  adrRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  posIcon: { width: 42, height: 42, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.card, alignItems: 'center', justifyContent: 'center' },
  sugg: { marginTop: 6, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, overflow: 'hidden' },
  suggItem: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 11, borderBottomWidth: 0.5, borderBottomColor: UI.border },
  suggNom: { fontSize: 13.5, fontWeight: '700', color: UI.ink },
  suggSous: { fontSize: 11.5, color: UI.muted, marginTop: 1 },
});
